import React, { useCallback, useEffect, useRef, useState } from "react";
import Popover from "../datepicker/Popover";
import RangePanel from "../datepicker/RangePanel";
import { rangePresets } from "../datepicker/calendar.mjs";
import useIsMobile from "../../hooks/useIsMobile";
import { api } from "../../api";
import ContactDetailPage from "../ContactDetailPage";
import { clickWords } from "../analytics/analyticsModel.mjs";

/*
 * INBOUND > LANDING PAGES > ANALYTICS, from his Claude Design mockup of
 * 2026-10-13. One question: which LinkedIn ads, and which version of the page,
 * turn clicks into booked calls. The numbers come from src/landing/analytics.js.
 *
 * Where it departs from the mockup, and why:
 *   - Spend, cost per fill and cost per call are not shown. The auditor keeps
 *     only whole-account daily spend; per ad set is behind its own login. The
 *     columns come back when it stores per-ad-set spend, not as a row of dashes.
 *   - No creative thumbnails: nothing here knows which image an ad used.
 *   - The campaign group is what LinkedIn calls it ("DR - LP"), and retargeting
 *     ad sets carry their own group or none, never the DR one.
 *   - A form fill opens the lead's FULL record beside the list, the same panel
 *     Leads uses, rather than a summary card of its own.
 *   - "What visitors did" (asked 2026-11-03, no mockup; built from this tab's
 *     own cards and tables): how far down each version is read, what gets
 *     clicked, and each recent visit with its scroll and clicks.
 */

const DIMS = [
  { key: "campaign", label: "Campaign", head: "Campaign (ad set)" },
  { key: "ad", label: "Ad", head: "Ad" },
  { key: "group", label: "Campaign group", head: "Campaign group" },
  { key: "source", label: "Source / medium", head: "Source / medium" },
  { key: "version", label: "Page version", head: "Version" },
];
const TRAFFIC = [
  { key: "ads", label: "From ads" },
  { key: "other", label: "Other" },
  { key: "all", label: "All" },
];
const CAPTION = {
  ads: "Visits whose link carried LinkedIn's tags",
  other: "Direct and untagged visits only",
  all: "Every visit, ads and other together",
};

const ZONE = "Asia/Jerusalem";
const shortDate = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    timeZone: ZONE,
  });
const dayLabel = (key) => {
  const d = new Date(`${key}T12:00:00Z`);
  return {
    dow: d.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" }),
    date: d.toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      timeZone: "UTC",
    }),
  };
};
const isoDay = (d) => new Date(d).toLocaleDateString("en-CA", { timeZone: ZONE });
const pct = (n) => (n == null ? "–" : `${n}%`);
const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function rangeLabel(a) {
  const from = shortDate(a.range.from);
  const to = shortDate(a.range.to);
  return from === to ? from : `${from} to ${to}`;
}

/* ------------------------------------------------------------ filters -- */

function RangeFilter({ data, range, onChange }) {
  const [open, setOpen] = useState(false);
  const btn = useRef(null);
  const mobile = useIsMobile();
  const close = useCallback(() => setOpen(false), []);
  const today = isoDay(new Date());

  const lastDays = (n) => {
    close();
    onChange({
      preset: `${n}d`,
      from: new Date(Date.now() - n * 86400000).toISOString(),
      to: null,
    });
  };
  // Whole days in his time: midnight to the next midnight, Israel.
  const apply = ({ from, to }) => {
    close();
    const start = new Date(`${from}T00:00:00+03:00`).toISOString();
    const end = new Date(new Date(`${to}T00:00:00+03:00`).getTime() + 86400000).toISOString();
    return onChange({ preset: "custom", from: start, to: end });
  };
  const presets = [
    {
      key: "default",
      label: data.range.default_label,
      hint: `from ${shortDate(data.range.default_from)}`,
      onSelect: () => {
        close();
        onChange(null);
      },
    },
    { key: "7d", label: "Last 7 days", onSelect: () => lastDays(7) },
    { key: "30d", label: "Last 30 days", onSelect: () => lastDays(30) },
    ...rangePresets(["thisWeek", "lastWeek", "thisMonth", "lastMonth"], today),
  ];

  const label = !range ? data.range.default_label : range.preset === "custom" ? "Custom" : `Last ${range.preset.replace("d", " days")}`;
  return (
    <span className="crm-lpa__range">
      <button ref={btn} type="button" className="crm-lpa__fbtn" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(!open)}>
        <span>{label}</span> <b>{rangeLabel(data)}</b> ▾
      </button>
      {open && (
        <Popover anchor={btn} onClose={close} label="Pick dates">
          <RangePanel
            from={isoDay(data.range.from)}
            to={isoDay(data.range.to)}
            max={today}
            presets={presets}
            activeKey={!range ? "default" : range.preset !== "custom" ? range.preset : undefined}
            onApply={apply}
            onCancel={close}
            mobile={mobile}
          />
        </Popover>
      )}
    </span>
  );
}

function Seg({ label, options, value, onChange }) {
  return (
    <span className="crm-seg crm-lpa__seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button key={o.key ?? "all"} type="button" className={value === o.key ? "sel" : ""} aria-pressed={value === o.key} onClick={() => onChange(o.key)}>
          {o.label}
        </button>
      ))}
    </span>
  );
}

/* ------------------------------------------------------------- funnel -- */

function Rate({ value, small, of }) {
  return (
    <span className="crm-lpa__rate" title={small ? `Small sample: ${of}` : undefined}>
      <b>
        {small && value != null && <span className="crm-lpa__tilde">~</span>}
        {pct(value)}
      </b>
    </span>
  );
}

function Funnel({ data }) {
  const ads = data.funnel.ads;
  const other = data.funnel.other;
  const all = data.funnel.all;
  const fills = data.fills.filter((f) => f.from_ads);
  const who =
    fills.length === 1
      ? `${fills[0].name}${fills[0].company ? `, ${fills[0].company}` : ""}`
      : fills.length
        ? plural(fills.length, "person", "people")
        : "none yet";
  return (
    <section className="crm-lpa__funnelrow" aria-label="Funnel">
      <div className="crm-card crm-lpa__funnel">
        <div className="crm-lpa__cardhead">
          <h3>From ads</h3>
          <span className="crm-hint">{CAPTION.ads}</span>
        </div>
        <div className="crm-lpa__steps">
          <div className="crm-lpa__step">
            <span className="crm-label">Visits</span>
            <b className="crm-lpa__big">{ads.visits}</b>
            <span className="crm-hint">{plural(ads.people, "person", "people")}</span>
          </div>
          <div className="crm-lpa__arrow">
            <Rate value={ads.fill_rate} small={ads.small} of={plural(ads.visits, "visit")} />
            <span className="crm-hint">fill rate</span>
          </div>
          <div className="crm-lpa__step is-key">
            <span className="crm-label">Form fills</span>
            <b className="crm-lpa__big">{ads.fills}</b>
            <span className="crm-lpa__who">{who}</span>
          </div>
          <div className="crm-lpa__arrow">
            <b>{ads.fills ? `${ads.calls} of ${ads.fills}` : "–"}</b>
            <span className="crm-hint">booked</span>
          </div>
          <div className="crm-lpa__step">
            <span className="crm-label">Booked calls</span>
            <b className="crm-lpa__big">{ads.calls}</b>
            <span className="crm-hint">{ads.open_fills ? `${plural(ads.open_fills, "fill")} still open` : ads.fills ? "every fill booked" : " "}</span>
          </div>
        </div>
        <FunnelNote f={ads} />
      </div>
      <div className="crm-card crm-lpa__other">
        <h4>Other traffic</h4>
        <div className="crm-lpa__minis">
          <div>
            <b>{other.visits}</b>
            <span className="crm-hint">visits</span>
          </div>
          <div>
            <b>{other.people}</b>
            <span className="crm-hint">people</span>
          </div>
          <div>
            <b>{other.fills}</b>
            <span className="crm-hint">fills</span>
          </div>
        </div>
        <p className="crm-hint">
          Direct and untagged visits: the team checking the page, link checkers, anyone typing the address. Kept out of the ad numbers.
        </p>
        <div className="crm-lpa__allline">
          All traffic: <b>{all.visits}</b> visits, <b>{all.people}</b> people, <b>{all.fills}</b> {all.fills === 1 ? "fill" : "fills"}, <b>{all.calls}</b>{" "}
          {all.calls === 1 ? "call" : "calls"}
        </div>
        {data.unrun && data.unrun.visits > 0 && (
          <p className="crm-hint">
            Not counted anywhere here: {plural(data.unrun.visits, "page load")} that never ran the page (scanners, link checkers)
            {data.unrun.from_ads ? `, ${data.unrun.from_ads} of them on ad links` : ""}.
          </p>
        )}
      </div>
    </section>
  );
}

function FunnelNote({ f }) {
  if (!f.visits) return <p className="crm-lpa__note">No ad clicks in this range yet.</p>;
  if (!f.small) {
    return f.fill_ci ? (
      <p className="crm-lpa__note">
        {plural(f.fills, "fill")} in {f.visits} visits: the true fill rate is likely between {f.fill_ci[0]}% and {f.fill_ci[1]}%.
      </p>
    ) : null;
  }
  const step = Math.round(1000 / (f.visits + 1)) / 10;
  return (
    <p className="crm-lpa__note">
      <b>Too early to call.</b>{" "}
      {f.fills
        ? `${plural(f.fills, "fill")} in ${f.visits} visits fits a true fill rate anywhere from about ${Math.round(f.fill_ci[0])}% to ${Math.round(f.fill_ci[1])}%.`
        : `No fills in ${f.visits} visits fits a true fill rate anywhere up to about ${Math.round(f.fill_ci[1])}%.`}{" "}
      At this volume, one more fill moves the rate by about {Math.round(step)} points.
    </p>
  );
}

/* ---------------------------------------------------------- breakdown -- */

function Breakdown({ data, dim, setDim }) {
  const d = DIMS.find((x) => x.key === dim);
  const rows = data.breakdown[dim] || [];
  const tot = data.funnel[data.traffic];
  return (
    <section className="crm-card crm-lpa__breakdown" aria-label="Breakdown">
      <div className="crm-lpa__cardhead crm-lpa__cardhead--split">
        <div>
          <h3>Breakdown</h3>
          <span className="crm-hint">{CAPTION[data.traffic]}</span>
        </div>
        <Seg label="Rows show" options={DIMS} value={dim} onChange={setDim} />
      </div>
      <div className="crm-lpa__tablewrap">
        <table className="crm-table crm-lpa__table">
          <thead>
            <tr>
              <th>{d.head}</th>
              <th>Visits</th>
              <th>People</th>
              <th>Fills</th>
              <th>Fill rate</th>
              <th>Calls</th>
              <th>Call rate</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key || "_untagged"} className={!r.visits && !r.fills ? "is-zero" : ""}>
                <td>
                  <span className="crm-lpa__namecell">
                    {r.variant && <span className={`crm-lpa__vb${r.variant.live ? " is-live" : ""}`}>{r.variant.key.toUpperCase()}</span>}
                    <span className="crm-lpa__names">
                      <span className={`crm-lpa__trunc${r.untagged ? " is-untagged" : ""}`} title={r.name}>
                        {r.name}
                      </span>
                      {r.sub && (
                        <span className="crm-lpa__sub" title={r.sub}>
                          {r.sub}
                        </span>
                      )}
                    </span>
                  </span>
                </td>
                <td>{r.visits}</td>
                <td>{r.people}</td>
                <td>{r.fills}</td>
                <td title={r.small ? `Small sample: ${plural(r.visits, "visit")}` : undefined}>
                  {r.small && r.visits ? <span className="crm-lpa__tilde">~</span> : null}
                  {pct(r.fill_rate)}
                </td>
                <td>{r.calls}</td>
                <td>{pct(r.call_rate)}</td>
              </tr>
            ))}
            {!rows.length && (
              <tr>
                <td colSpan={7} className="crm-empty">
                  Nothing in this range.
                </td>
              </tr>
            )}
          </tbody>
          {rows.length > 1 && (
            <tfoot>
              <tr>
                <td>Total</td>
                <td>{tot.visits}</td>
                <td>{tot.people}</td>
                <td>{tot.fills}</td>
                <td>
                  {tot.small && tot.visits ? <span className="crm-lpa__tilde">~</span> : null}
                  {pct(tot.fill_rate)}
                </td>
                <td>{tot.calls}</td>
                <td>{pct(tot.call_rate)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <div className="crm-lpa__foot">
        <span>
          <b>~</b> small sample: under 30 visits, read it as a hint
        </span>
        <span>Call rate is calls per fill</span>
        <span>Spend per campaign is not connected yet</span>
      </div>
    </section>
  );
}

/* -------------------------------------------------------------- trend -- */

function niceMax(n) {
  if (n <= 5) return 5;
  if (n <= 10) return 10;
  const step = n <= 30 ? 10 : n <= 60 ? 20 : n <= 150 ? 50 : 100;
  return Math.ceil(n / step) * step;
}

function Trend({ data }) {
  const days = data.days;
  const max = niceMax(Math.max(1, ...days.map((d) => d.ads + d.other)));
  const ticks = [max, Math.round((max * 2) / 3), Math.round(max / 3), 0];
  const today = isoDay(new Date());
  const summary = `Daily visits, ${rangeLabel(data)}. From ads: ${days.map((d) => d.ads).join(", ")}. Other: ${days.map((d) => d.other).join(", ")}.`;
  const vkey = (p) => p.variant.toUpperCase();
  return (
    <section className="crm-card crm-lpa__trend" aria-label="Daily visits">
      <div className="crm-lpa__cardhead crm-lpa__cardhead--split">
        <h3>Daily visits</h3>
        <span className="crm-lpa__legend">
          <span>
            <i className="is-ads" />
            From ads
          </span>
          <span>
            <i className="is-other" />
            Other
          </span>
          <span>
            <i className="is-fill" />
            Form fill
          </span>
          <span>
            <i className="is-call" />
            Booked call
          </span>
          <span>
            <i className="is-version" />
            Version published
          </span>
        </span>
      </div>
      <div className="crm-lpa__chart" role="img" aria-label={summary}>
        <div className="crm-lpa__axis" aria-hidden="true">
          {ticks.map((t) => (
            <span key={t}>{t}</span>
          ))}
        </div>
        <div className="crm-lpa__plot">
          <div className="crm-lpa__grid" aria-hidden="true">
            {ticks.map((t) => (
              <span key={t} />
            ))}
          </div>
          <div className="crm-lpa__bars">
            {days.map((d) => {
              const l = dayLabel(d.day);
              const tip = [
                `${l.dow} ${l.date}${d.day === today ? " (so far)" : ""}`,
                `From ads: ${d.ads}`,
                `Other: ${d.other}`,
                d.fills ? `Form fills: ${d.fills}` : null,
                d.calls ? `Booked calls: ${d.calls}` : null,
                ...d.published.map((p) => (p.first ? `${vkey(p)} went live` : `${vkey(p)} v${p.version} published`)),
              ].filter(Boolean);
              return (
                <div className="crm-lpa__day" key={d.day} tabIndex={0} aria-label={tip.join(", ")}>
                  {d.published.length > 0 && (
                    <span className="crm-lpa__vline">
                      <span>{d.published.map((p) => (p.first ? `${vkey(p)} live` : `${vkey(p)} v${p.version}`)).join(" · ")}</span>
                    </span>
                  )}
                  <span className="crm-lpa__stack" style={{ height: `${((d.ads + d.other) / max) * 100}%` }}>
                    {(d.fills > 0 || d.calls > 0) && (
                      <span className="crm-lpa__marks">
                        {d.fills > 0 && <span className="crm-lpa__mark is-fill">{plural(d.fills, "fill")}</span>}
                        {d.calls > 0 && <span className="crm-lpa__mark is-call">{plural(d.calls, "call")}</span>}
                      </span>
                    )}
                    {d.other > 0 && <i className="crm-lpa__seg-other" style={{ flexGrow: d.other }} />}
                    {d.ads > 0 && <i className="crm-lpa__seg-ads" style={{ flexGrow: d.ads }} />}
                  </span>
                  <span className="crm-lpa__tip" role="tooltip">
                    {tip.map((t, i) => (i === 0 ? <b key={t}>{t}</b> : <span key={t}>{t}</span>))}
                  </span>
                </div>
              );
            })}
          </div>
          <div className="crm-lpa__xlabels" aria-hidden="true">
            {days.map((d) => {
              const l = dayLabel(d.day);
              return (
                <span key={d.day}>
                  <span>
                    {l.dow} {l.date}
                  </span>
                  <b>
                    {d.ads} + {d.other}
                  </b>
                </span>
              );
            })}
          </div>
        </div>
      </div>
      <p className="crm-hint">Under each day: from ads + other. Today is still filling in. Days are Israel time.</p>
    </section>
  );
}

/* ---------------------------------------------------------- engagement -- */

const ENGAGE_FROM = "3 Nov"; // the first beat that reported scroll and clicks (migration 085)
const KIND_WORDS = { link: "Link", button: "Button", field: "Form field", other: "Not a button" };
const when = (iso) =>
  new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: ZONE });
const dur = (s) => (s == null ? "–" : s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, "0")}s`);

function Reach({ share }) {
  return (
    <span className="crm-lpa__reach">
      <span className="crm-lpa__reachbar" aria-hidden="true">
        <span style={{ width: `${share || 0}%` }} />
      </span>
      <span>{pct(share)}</span>
    </span>
  );
}

function Engagement({ data }) {
  const e = data.engagement;
  if (!e) return null;
  if (!e.recorded) {
    return (
      <section className="crm-card crm-lpa__engage" aria-label="What visitors did">
        <div className="crm-lpa__cardhead">
          <h3>What visitors did</h3>
        </div>
        <p className="crm-hint">How far down people scroll and what they click is recorded for visits from {ENGAGE_FROM} on. None in this range yet.</p>
      </section>
    );
  }
  const vs = e.variants;
  const many = vs.length > 1;
  const depth = Math.max(0, ...vs.map((v) => v.sections.length));
  const rows = [];
  for (let n = 1; n <= depth; n++) {
    const named = vs.map((v) => v.sections[n - 1]).find((x) => x && x.label);
    rows.push({ n, label: named ? named.label : `Section ${n}` });
  }
  const vkey = (v) => v.key || "_";
  const vname = (v) => (v.key || "?").toUpperCase();
  return (
    <>
      <section className="crm-card crm-lpa__engage" aria-label="What visitors did">
        <div className="crm-lpa__cardhead crm-lpa__cardhead--split">
          <div>
            <h3>What visitors did</h3>
            <span className="crm-hint">
              {CAPTION[data.traffic]}. Recorded from {ENGAGE_FROM} on: {e.recorded} of {plural(e.of, "visit")} in this range.
            </span>
          </div>
        </div>
        <div className="crm-lpa__tablewrap">
          <table className="crm-table crm-lpa__table">
            <thead>
              <tr>
                <th>Version</th>
                <th>Visits</th>
                <th title="Half the visits got further down than this">Median scroll</th>
                <th title="Saw 90% of the page or more">Reached the bottom</th>
                <th title="Clicked a link, a button or anything else on the page">Clicked something</th>
                <th title="Put the cursor in a form field; what they typed is never recorded">Started the form</th>
              </tr>
            </thead>
            <tbody>
              {vs.map((v) => (
                <tr key={vkey(v)}>
                  <td>
                    <span className="crm-lpa__namecell">
                      {v.key && <span className="crm-lpa__vb is-live">{vname(v)}</span>}
                      <span className="crm-lpa__names">
                        <span className="crm-lpa__trunc">{v.label || "Unknown version"}</span>
                      </span>
                    </span>
                  </td>
                  <td>{v.recorded}</td>
                  <td>{pct(v.median_scroll)}</td>
                  <td>
                    {v.bottom} <span className="crm-hint">({pct(v.bottom_share)})</span>
                  </td>
                  <td>
                    {v.clicked} <span className="crm-hint">({pct(v.clicked_share)})</span>
                  </td>
                  <td>
                    {v.started_form} <span className="crm-hint">({pct(v.started_share)})</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {depth > 0 && (
          <>
            <h4 className="crm-lpa__subhead">How far down they got</h4>
            <div className="crm-lpa__tablewrap">
              <table className="crm-table crm-lpa__table">
                <thead>
                  <tr>
                    <th>Section, top to bottom</th>
                    {vs.map((v) => (
                      <th key={vkey(v)}>{many ? `Reached on ${vname(v)}` : "Reached"}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.n}>
                      <td>
                        <span className="crm-lpa__trunc" title={r.label}>
                          {r.n}. {r.label}
                        </span>
                      </td>
                      {vs.map((v) => {
                        const sec = v.sections[r.n - 1];
                        return (
                          <td key={vkey(v)} title={sec && sec.label && sec.label !== r.label ? `On ${vname(v)}: ${sec.label}` : undefined}>
                            {sec ? <Reach share={sec.share} /> : <span className="crm-hint">–</span>}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
        <div className="crm-lpa__foot">
          <span>Scroll is the furthest share of the page that was on screen</span>
          <span>A section is reached once its top is a quarter of the way up the screen</span>
        </div>
      </section>

      <section className="crm-card crm-lpa__engage" aria-label="What they clicked">
        <div className="crm-lpa__cardhead">
          <h3>What they clicked</h3>
          <span className="crm-hint">Each thing counted once per visit</span>
        </div>
        {e.targets.length ? (
          <div className="crm-lpa__tablewrap">
            <table className="crm-table crm-lpa__table">
              <thead>
                <tr>
                  <th>What</th>
                  <th className="crm-lpa__left">Where on the page</th>
                  <th>Visits</th>
                  {many && vs.map((v) => <th key={vkey(v)}>{vname(v)}</th>)}
                </tr>
              </thead>
              <tbody>
                {e.targets.map((t) => (
                  <tr key={`${t.kind}|${t.label}|${t.section || ""}`}>
                    <td>
                      <span className="crm-lpa__names">
                        <span className="crm-lpa__trunc" title={t.label}>
                          {clickWords({ k: t.kind, l: t.label })}
                        </span>
                        <span className="crm-lpa__sub" title={t.href || undefined}>
                          {KIND_WORDS[t.kind]}
                          {t.href ? ` to ${t.href}` : ""}
                        </span>
                      </span>
                    </td>
                    <td className="crm-lpa__left">
                      <span className="crm-lpa__sub" title={t.section || undefined}>
                        {t.section || "–"}
                      </span>
                    </td>
                    <td>
                      {t.visits}
                      {t.clicks > t.visits && <span className="crm-hint"> ({plural(t.clicks, "click")})</span>}
                    </td>
                    {many && vs.map((v) => <td key={vkey(v)}>{t.by_variant[v.key] || 0}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="crm-hint">Nobody clicked anything in this range.</p>
        )}
      </section>

      <section className="crm-card crm-lpa__engage" aria-label="Recent visits">
        <div className="crm-lpa__cardhead">
          <h3>Recent visits</h3>
          <span className="crm-hint">Newest {e.recent.length}, with or without a form</span>
        </div>
        <div className="crm-lpa__tablewrap">
          <table className="crm-table crm-lpa__filltable crm-lpa__visittable">
            <thead>
              <tr>
                <th>When</th>
                <th>Version</th>
                <th>Campaign</th>
                <th>Time on page</th>
                <th>Got to</th>
                <th>Clicked</th>
                <th>Form</th>
              </tr>
            </thead>
            <tbody>
              {e.recent.map((v) => (
                <tr key={v.id}>
                  <td className="crm-lpa__nowrap">{when(v.at)}</td>
                  <td>{v.variant ? <span className="crm-lpa__vb">{v.variant.toUpperCase()}</span> : "–"}</td>
                  <td>
                    {v.campaign ? (
                      <span className="crm-lpa__sub" title={v.campaign}>
                        {v.campaign}
                      </span>
                    ) : (
                      <span className="crm-hint">{v.from_ads ? "Ad, no campaign tag" : "Not from an ad"}</span>
                    )}
                  </td>
                  <td>{dur(v.seconds)}</td>
                  <td>
                    <b>{pct(v.scroll_pct)}</b>
                    {v.section && (
                      <div className="crm-lpa__sub" title={v.section}>
                        {v.section_n}. {v.section}
                      </div>
                    )}
                  </td>
                  <td>
                    {v.clicks.length ? (
                      <ol className="crm-lpa__clicks">
                        {v.clicks.map((c, i) => (
                          <li key={i} title={[c.s && `In “${c.s}”`, c.t != null && `${dur(c.t)} in`, c.h && `to ${c.h}`].filter(Boolean).join(" · ")}>
                            {clickWords(c)}
                          </li>
                        ))}
                      </ol>
                    ) : (
                      <span className="crm-hint">Nothing</span>
                    )}
                  </td>
                  <td>{v.filled ? <span className="crm-pill crm-pill--active">Sent</span> : <span className="crm-hint">–</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="crm-hint">Hover a click for where it was and how long into the visit. A form field shows it was used, never what was typed.</p>
      </section>
    </>
  );
}

/* -------------------------------------------------------------- fills -- */

function Fills({ data, openId, onOpen }) {
  const fills = data.fills;
  return (
    <section className="crm-card crm-lpa__fills" aria-label="Recent form fills">
      <div className="crm-lpa__cardhead">
        <h3>Recent form fills</h3>
        <span className="crm-hint">{fills.length ? `${fills.length} in this range` : "None in this range"}</span>
      </div>
      {fills.length > 0 && (
        <div className="crm-lpa__tablewrap">
          <table className="crm-table crm-lpa__filltable">
            <thead>
              <tr>
                <th>Name</th>
                <th>Campaign and ad</th>
                <th>Version</th>
                <th>When</th>
                <th>Booked</th>
              </tr>
            </thead>
            <tbody>
              {fills.map((f) => (
                <tr
                  key={f.id}
                  className={`${f.contact_id ? "crm-tr--link" : ""}${openId && openId === f.contact_id ? " crm-tr--open" : ""}`}
                  onClick={() => f.contact_id && onOpen(openId === f.contact_id ? null : f.contact_id)}
                >
                  <td>
                    <b>{f.name}</b>
                    {f.company && <div className="crm-lpa__sub">{f.company}</div>}
                  </td>
                  <td>
                    {f.campaign ? (
                      <>
                        <div className="crm-lpa__trunc">{f.campaign}</div>
                        {f.ad && (
                          <div className="crm-lpa__sub" title={f.ad}>
                            {f.ad}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="crm-hint">{f.source ? `${f.source}, no campaign tag` : "Not from an ad"}</span>
                    )}
                  </td>
                  <td>{f.variant ? <span className={`crm-lpa__vb${f.variant.live ? " is-live" : ""}`}>{f.variant.key.toUpperCase()}</span> : "–"}</td>
                  <td>{shortDate(f.created_at)}</td>
                  <td>
                    {f.booked ? (
                      <span className="crm-pill crm-pill--active">
                        Booked
                        {f.booking_starts_at ? `, ${shortDate(f.booking_starts_at)}` : ""}
                      </span>
                    ) : (
                      <span className="crm-pill crm-pill--draft">Not yet</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {fills.length > 0 && !openId && <p className="crm-hint">Click a row to open the lead beside the list.</p>}
    </section>
  );
}

/* --------------------------------------------------------------- page -- */

export default function LandingAnalytics({ pages }) {
  const choices = pages.filter((p) => p.status !== "archived");
  const [pageId, setPageId] = useState(() => (choices.find((p) => p.status === "live") || choices[0] || {}).id || null);
  const [range, setRange] = useState(null);
  const [variant, setVariant] = useState(null);
  const [traffic, setTraffic] = useState("ads");
  const [dim, setDim] = useState("campaign");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [peek, setPeek] = useState(null);

  useEffect(() => {
    if (!pageId) return undefined;
    let live = true;
    const q = new URLSearchParams({ traffic });
    if (range && range.from) q.set("from", range.from);
    if (range && range.to) q.set("to", range.to);
    if (variant) q.set("variant", variant);
    setBusy(true);
    api(`/landing-pages/${pageId}/analytics?${q}`)
      .then((d) => live && (setData(d), setError(null)))
      .catch((err) => live && setError(err.message))
      .finally(() => live && setBusy(false));
    return () => {
      live = false;
    };
  }, [pageId, range, variant, traffic]);

  if (!pageId) return <div className="crm-card crm-lpa__empty">No landing pages yet.</div>;
  if (error && !data) return <div className="crm-card crm-card--warning">{error}</div>;
  if (!data) return <AnalyticsLoading />;

  const versions = [{ key: null, label: "All" }, ...data.variants.map((v) => ({ key: v.key, label: v.key.toUpperCase() }))];
  const noTraffic = !data.funnel.all.visits && !data.fills.length;

  return (
    <div className={`crm-lpa${busy ? " is-busy" : ""}`}>
      <div className="crm-lpa__filters">
        <RangeFilter data={data} range={range} onChange={setRange} />
        <label className="crm-lpa__fbtn crm-lpa__pagepick">
          <span>Page</span>
          <select
            value={pageId}
            onChange={(e) => {
              setPageId(Number(e.target.value));
              setVariant(null);
              setRange(null);
              setPeek(null);
            }}
          >
            {choices.map((p) => (
              <option key={p.id} value={p.id}>
                /{p.slug}
              </option>
            ))}
          </select>
        </label>
        <Seg label="Version" options={versions} value={variant} onChange={setVariant} />
        <Seg label="Traffic" options={TRAFFIC} value={traffic} onChange={setTraffic} />
      </div>

      {noTraffic ? (
        <div className="crm-card crm-lpa__empty">
          <h3>No traffic yet</h3>
          <p className="crm-hint">
            Nobody has visited /{data.page.slug} {range ? "in this range" : "since it went live"}. Point an ad at{" "}
            {data.page.slug === "linkedin-ads" ? "lp.remotion.io/linkedin-ads" : `lp.remotion.io/${data.page.slug}`} and visits show up here within seconds.
            Previews never count.
          </p>
        </div>
      ) : (
        <div className={`crm-lpa__layout${peek ? " has-peek" : ""}`}>
          <Funnel data={data} />
          <Breakdown data={data} dim={dim} setDim={setDim} />
          <Trend data={data} />
          <Engagement data={data} />
          <Fills data={data} openId={peek} onOpen={setPeek} />
        </div>
      )}

      {peek && (
        <aside className="crm-peek crm-lpa__peek">
          <ContactDetailPage key={peek} contactId={peek} embedded onClose={() => setPeek(null)} hostLabel="Landing pages" />
        </aside>
      )}
    </div>
  );
}

function AnalyticsLoading() {
  return (
    <div className="crm-lpa" aria-busy="true" aria-label="Loading analytics">
      <div className="crm-lpa__filters">
        <span className="crm-lpa__skel" style={{ width: 220, height: 40 }} />
        <span className="crm-lpa__skel" style={{ width: 240, height: 40 }} />
        <span className="crm-lpa__skel" style={{ width: 160, height: 40 }} />
      </div>
      <div className="crm-lpa__funnelrow">
        <span className="crm-lpa__skel" style={{ flex: "999 1 560px", height: 260 }} />
        <span className="crm-lpa__skel" style={{ flex: "1 1 260px", height: 260 }} />
      </div>
      <span className="crm-lpa__skel" style={{ height: 280 }} />
    </div>
  );
}
